/**
 * Example project: a small Belgian house.
 * 1. Single-line diagram (generated, RGIE style) with circuits A–G.
 * 2. Start/stop control circuit for the ventilation.
 * 3. Position plan with rooms and the same point codes as the single-line diagram.
 */
import {
  applyTransaction,
  createProject,
  newId,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type WireEnd,
  tr,
  type Locale,
} from '@elec/domain';
import { generateBoardOps, type BoardSpec } from '@elec/editor';

export function demoBoard(locale: Locale): BoardSpec {
  const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
  const sockets = (n: number, props: Record<string, PropValue> = {}) =>
    Array.from({ length: n }, () => ({ symbolId: 'iec.socket', props }));
  const xvb = (s: number, lengthM: number) => ({ type: 'XVB', cores: 3, sectionMm2: s, lengthM });
  return {
    supply: { voltage: '230 V', ratingA: 40 },
    meter: true,
    main: { ratingA: 40, poles: 2 },
    mainRcd: { ratingA: 40, sensitivityMa: 300, rcdType: 'A', poles: 2 },
    groups: [
      {
        circuits: [
          {
            label: T('Prises séjour', 'Living room sockets', 'Stopcontacten woonkamer'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [...sockets(1, { count: 2 }), ...sockets(2), ...sockets(1, { count: 2 })],
            cable: xvb(2.5, 22),
          },
          {
            label: T('Éclairage', 'Lighting', 'Verlichting'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 16, curve: 'B' } },
            points: [
              { symbolId: 'iec.light' },
              { symbolId: 'iec.light', props: { count: 2 } },
              { symbolId: 'iec.light-wall' },
            ],
            cable: xvb(1.5, 18),
          },
          {
            label: T('Chauffe-eau', 'Water heater', 'Boiler'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.water-heater', props: { powerKw: 2.4 } }],
            cable: xvb(2.5, 12),
          },
          {
            label: T('Taque de cuisson', 'Hob', 'Kookplaat'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 32, curve: 'C' } },
            points: [{ symbolId: 'iec.cooker', props: { powerKw: 7 } }],
            cable: xvb(6, 9),
          },
        ],
      },
      {
        rcd: { ratingA: 40, sensitivityMa: 30, rcdType: 'A', poles: 2 },
        circuits: [
          {
            label: T('Salle de bain', 'Bathroom', 'Badkamer'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: sockets(2, { ip: 'IP44' }),
            cable: xvb(2.5, 14),
          },
          {
            label: T('Lave-linge', 'Washing machine', 'Wasmachine'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.washing-machine' }],
            cable: xvb(2.5, 10),
          },
          {
            label: T('Lave-vaisselle', 'Dishwasher', 'Vaatwasser'),
            device: { symbolId: 'iec.breaker', props: { ratingA: 20, curve: 'C' } },
            points: [{ symbolId: 'iec.dishwasher' }],
            cable: xvb(2.5, 8),
          },
        ],
      },
    ],
  };
}

export function generateDemoProject(locale: 'fr' | 'en' | 'nl' = 'fr'): Project {
  const T = (f: string, e: string, n: string) => tr(locale, f, e, n);
  let p = createProject({ title: T('Maison exemple', 'Example house', 'Voorbeeldwoning'), locale });
  const sl = Object.keys(p.sheets)[0]!;
  const ctl = newId();
  const plan = newId();
  const setup: Op[] = [
    {
      op: 'update',
      coll: 'sheets',
      id: sl,
      patch: { paper: 'A3', name: T('Tableau principal', 'Main board', 'Hoofdbord') },
    },
    {
      op: 'add',
      coll: 'sheets',
      value: {
        id: ctl,
        name: T('Commande ventilation', 'Ventilation control', 'Sturing ventilatie'),
        kind: 'control',
        paper: 'A4',
        orientation: 'landscape',
        order: 1,
      },
    },
    {
      op: 'add',
      coll: 'sheets',
      value: {
        id: plan,
        name: T('Plan rez-de-chaussée', 'Ground floor plan', 'Plan gelijkvloers'),
        kind: 'position-plan',
        paper: 'A3',
        orientation: 'landscape',
        order: 2,
        planScale: 50,
      },
    },
    {
      op: 'meta',
      patch: {
        ownerName: 'Marie Dupont',
        ownerAddress: 'Rue de la Station 12, 1300 Wavre',
        author: 'J. Peeters',
        company: 'Peeters Électricité',
        supplyVoltage: '230 V',
        earthingSystem: 'TT',
      },
    },
  ];
  p = applyTransaction(p, { label: '', ops: setup }).project;
  p = applyTransaction(p, { label: '', ops: generateBoardOps(p, sl, demoBoard(locale)) }).project;

  const ops: Op[] = [];
  const add = (
    sheetId: Id,
    symbolId: string,
    x: number,
    y: number,
    tag: string,
    props: Record<string, PropValue> = {},
  ): Id => {
    const id = newId();
    const el: Element = { id, sheetId, symbolId, x, y, rotation: 0, mirrored: false, tag, props };
    ops.push({ op: 'add', coll: 'elements', value: el });
    return id;
  };
  const text = (sheetId: Id, x: number, y: number, t: string, size = 3) =>
    add(sheetId, 'annot.text', x, y, '', { text: t, size });
  const port = (elementId: Id, portId: string): WireEnd => ({ kind: 'port', elementId, portId });
  const pt = (x: number, y: number): WireEnd => ({ kind: 'point', x, y });
  const wire = (sheetId: Id, from: WireEnd, to: WireEnd, via: Array<{ x: number; y: number }> = []) =>
    ops.push({ op: 'add', coll: 'wires', value: { id: newId(), sheetId, from, to, via } });

  add(sl, 'iec.earth-electrode', 70, 70, 'PE', { resistanceOhm: 28 });
  text(
    sl,
    48,
    21,
    T('Réseau GRD — 230 V monophasé', 'DSO supply — 230 V single-phase', 'Netbeheerder — 230 V eenfasig'),
    2.5,
  );

  // ---------- control: start/stop with self-holding contact ----------
  // Supply buses named as potentials (wire labels), so wire numbering leaves them alone.
  ops.push({
    op: 'add',
    coll: 'wires',
    value: { id: newId(), sheetId: ctl, from: pt(20, 30), to: pt(140, 30), via: [], label: 'L' },
  });
  ops.push({
    op: 'add',
    coll: 'wires',
    value: { id: newId(), sheetId: ctl, from: pt(20, 140), to: pt(140, 140), via: [], label: 'N' },
  });
  const f1 = add(ctl, 'iec.fuse', 35, 35, 'F1', { ratingA: 2 });
  const s0 = add(ctl, 'iec.pushbutton-nc', 35, 60, 'S0');
  const s1 = add(ctl, 'iec.pushbutton-no', 35, 80, 'S1');
  const k1 = add(ctl, 'iec.coil', 35, 102.5, 'K1');
  const k1a = add(ctl, 'iec.contact-no', 65, 80, 'K1');
  const k1b = add(ctl, 'iec.contact-no', 105, 60, 'K1');
  const h1 = add(ctl, 'iec.signal-lamp', 105, 85, 'P1');
  wire(ctl, pt(40, 30), port(f1, 'in'));
  wire(ctl, port(f1, 'out'), port(s0, 'in'));
  wire(ctl, port(s0, 'out'), port(s1, 'in'));
  wire(ctl, port(s1, 'out'), port(k1, 'in'), [{ x: 40, y: 97.5 }]);
  wire(ctl, port(k1, 'out'), pt(40, 140));
  wire(ctl, pt(40, 77.5), port(k1a, 'in'), [{ x: 70, y: 77.5 }]);
  wire(ctl, port(k1a, 'out'), pt(40, 97.5), [{ x: 70, y: 97.5 }]);
  wire(ctl, pt(110, 30), port(k1b, 'in'));
  wire(ctl, port(k1b, 'out'), port(h1, 'in'));
  wire(ctl, port(h1, 'out'), pt(110, 140));
  text(ctl, 150, 60, T('S0 : arrêt · S1 : marche', 'S0: stop · S1: start', 'S0: stop · S1: start'), 3);
  text(
    ctl,
    150,
    67,
    T('K1 : contacteur ventilation', 'K1: ventilation contactor', 'K1: contactor ventilatie'),
    3,
  );
  text(ctl, 150, 74, T('P1 : voyant de marche', 'P1: running indicator', 'P1: signaallamp werking'), 3);

  // ---------- position plan (1:50): rooms and points with the single-line codes ----------
  const room = (x: number, y: number, w: number, h: number, name: string, roomType: string) =>
    add(plan, 'plan.room', x, y, '', { name, roomType, w, h });
  room(30, 30, 140, 100, T('Séjour', 'Living room', 'Woonkamer'), 'living');
  room(170, 30, 90, 100, T('Cuisine', 'Kitchen', 'Keuken'), 'kitchen');
  room(30, 130, 80, 70, T('Hall', 'Hall', 'Hal'), 'hall');
  room(110, 130, 70, 70, T('Salle de bain', 'Bathroom', 'Badkamer'), 'bathroom');
  room(180, 130, 80, 70, T('Buanderie', 'Laundry', 'Wasplaats'), 'laundry');
  const point = (
    symbolId: string,
    tag: string,
    x: number,
    y: number,
    props: Record<string, PropValue> = {},
  ) => add(plan, symbolId, x, y, tag, props);
  point('iec.socket', 'A1', 40, 40, { count: 2 });
  point('iec.socket', 'A2', 90, 40);
  point('iec.socket', 'A3', 150, 60);
  point('iec.socket', 'A4', 40, 110, { count: 2 });
  point('iec.light', 'B1', 95, 75);
  point('iec.light', 'B2', 210, 75, { count: 2 });
  point('iec.light-wall', 'B3', 60, 150);
  point('iec.water-heater', 'C1', 240, 180, { powerKw: 2.4 });
  point('iec.cooker', 'D1', 240, 40, { powerKw: 7 });
  point('iec.socket', 'E1', 120, 140, { ip: 'IP44' });
  point('iec.socket', 'E2', 160, 180, { ip: 'IP44' });
  point('iec.washing-machine', 'F1', 200, 180);
  point('iec.dishwasher', 'G1', 180, 110);
  add(plan, 'iec.board', 40, 180, 'TD', {});
  text(
    plan,
    32,
    210,
    T(
      'Échelle 1:50 — tableau dans le hall',
      'Scale 1:50 — board in the hall',
      'Schaal 1:50 — bord in de hal',
    ),
    2.5,
  );

  return applyTransaction(p, { label: 'demo', ops }).project;
}
